import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { AccessStatus } from "@/components/AccessStatus";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { UploadCloud, FileText, History, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/reports").then(({ data }) => setRecent(data.slice(0, 4))).catch(() => {});
  }, []);

  const access = stats?.access || user?.access;

  const cards = [
    { label: "Total Uploads", value: stats?.uploads ?? "—", icon: UploadCloud, testid: "stat-uploads" },
    { label: "Reports Generated", value: stats?.reports ?? "—", icon: FileText, testid: "stat-reports" },
    {
      label: "Access Type",
      value: access?.subscription_active
        ? "Subscription"
        : access?.credits > 0
        ? `${access.credits} credit${access.credits > 1 ? "s" : ""}`
        : access?.free_available
        ? "Complimentary"
        : "Locked",
      icon: Sparkles,
      testid: "stat-access",
    },
  ];

  return (
    <DashboardLayout
      title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
      subtitle="Your communication intelligence workspace."
      action={
        <Button
          asChild
          data-testid="dashboard-new-analysis"
          className="bg-cyan-400 text-black font-semibold hover:bg-cyan-300 gx-glow"
        >
          <Link to="/upload">
            <UploadCloud className="h-4 w-4 mr-2" /> New Analysis
          </Link>
        </Button>
      }
    >
      <div className="space-y-8">
        <AccessStatus access={access} />

        <div className="grid gap-4 sm:grid-cols-3">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                data-testid={c.testid}
                className="rounded-xl border border-slate-800 bg-[#0A1124] p-6 transition-colors hover:border-slate-700"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-400">{c.label}</p>
                  <Icon className="h-4 w-4 text-cyan-400" />
                </div>
                <p className="mt-3 font-display text-3xl font-bold text-white">{c.value}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#0A1124]">
          <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
            <h3 className="font-display text-lg font-semibold text-white">Recent Reports</h3>
            <Link to="/reports" className="flex items-center gap-1 text-sm text-cyan-400 hover:underline" data-testid="view-all-reports">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-700 bg-slate-900">
                <History className="h-5 w-5 text-slate-500" />
              </div>
              <p className="text-slate-400">No reports yet. Upload your first conversation to get started.</p>
              <Button asChild variant="outline" className="border-slate-700 text-white hover:bg-slate-800 hover:text-white">
                <Link to="/upload" data-testid="empty-upload-cta">Upload audio</Link>
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-slate-800">
              {recent.map((r) => (
                <li key={r.report_id}>
                  <Link
                    to={`/reports/${r.report_id}`}
                    data-testid={`recent-report-${r.report_id}`}
                    className="flex items-center justify-between px-6 py-4 transition-colors hover:bg-slate-800/40"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-white">{r.filename}</p>
                      <p className="text-xs text-slate-500 font-mono">{formatDate(r.created_at)}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-500" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
